import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  GetCurrentSystemVersion,
  GetSystemVersions,
  CreateSystemVersion,
  UpdateSystemVersion,
  DeleteSystemVersion,
  RunMigrations,
  RunSeeds,
} from "../Api/system_version_api";
import { unwrapApiResponse } from "./query_helpers";
import { ApiResponse } from "../Types";

const DEFAULT_STALE_TIME = 30_000;

export const SYSTEM_VERSION_QUERY_KEY = ["system-version"];
export const SYSTEM_VERSIONS_LIST_QUERY_KEY = ["system-versions-list"];

export const useCurrentSystemVersionQuery = () =>
  useQuery({
    queryKey: SYSTEM_VERSION_QUERY_KEY,
    queryFn: async () => unwrapApiResponse(await GetCurrentSystemVersion()),
    staleTime: DEFAULT_STALE_TIME,
  });

export const useSystemVersionsQuery = () =>
  useQuery({
    queryKey: SYSTEM_VERSIONS_LIST_QUERY_KEY,
    queryFn: async () => unwrapApiResponse(await GetSystemVersions()),
    staleTime: DEFAULT_STALE_TIME,
  });

export const useCreateSystemVersionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { version: string; notes?: string }) =>
      CreateSystemVersion(payload),
    onSuccess: (res: ApiResponse) => {
      if (res?.success) {
        queryClient.invalidateQueries({
          queryKey: SYSTEM_VERSIONS_LIST_QUERY_KEY,
        });
        queryClient.invalidateQueries({ queryKey: SYSTEM_VERSION_QUERY_KEY });
      }
    },
  });
};

export const useUpdateSystemVersionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { id: string; version?: string; notes?: string }) =>
      UpdateSystemVersion(payload),
    onSuccess: (res: ApiResponse) => {
      if (res?.success) {
        queryClient.invalidateQueries({
          queryKey: SYSTEM_VERSIONS_LIST_QUERY_KEY,
        });
        queryClient.invalidateQueries({ queryKey: SYSTEM_VERSION_QUERY_KEY });
      }
    },
  });
};

export const useDeleteSystemVersionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => DeleteSystemVersion(id),
    onSuccess: (res: ApiResponse) => {
      if (res?.success) {
        queryClient.invalidateQueries({
          queryKey: SYSTEM_VERSIONS_LIST_QUERY_KEY,
        });
        queryClient.invalidateQueries({ queryKey: SYSTEM_VERSION_QUERY_KEY });
      }
    },
  });
};

export const useRunMigrationsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => RunMigrations(),
    onSuccess: (res: ApiResponse) => {
      if (res?.success) {
        queryClient.invalidateQueries({ queryKey: SYSTEM_VERSION_QUERY_KEY });
        queryClient.invalidateQueries({ queryKey: SYSTEM_VERSIONS_LIST_QUERY_KEY });
      }
    },
  });
};

export const useRunSeedsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => RunSeeds(),
    onSuccess: (res: ApiResponse) => {
      if (res?.success) {
        queryClient.invalidateQueries({ queryKey: SYSTEM_VERSION_QUERY_KEY });
        queryClient.invalidateQueries({ queryKey: SYSTEM_VERSIONS_LIST_QUERY_KEY });
      }
    },
  });
};
